import { useCallback, useState } from 'react';
import { toPng } from 'html-to-image';
import {
  Document,
  Packer,
  Paragraph,
  TextRun,
  Table,
  TableRow,
  TableCell,
  ImageRun,
  Header,
  Footer,
  AlignmentType,
  HeadingLevel,
  BorderStyle,
  WidthType,
  ShadingType,
  PageNumber,
  PageBreak,
  LevelFormat,
  ExternalHyperlink,
  TableOfContents,
  PageOrientation,
  SectionType,
  VerticalAlign,
} from 'docx';

// ── Unit helpers ────────────────────────────────────
// DXA: 1 inch = 1440
const INCH = 1440;

function detectImageType(data) {
  if (typeof data === 'string') {
    const match = data.match(/^data:image\/(png|jpe?g|gif|webp|bmp|svg\+xml)/i);
    if (match) return match[1].replace('jpeg', 'jpg').replace('svg+xml', 'svg');
  }
  if (data instanceof Uint8Array) {
    if (data[0] === 0x89 && data[1] === 0x50) return 'png';
    if (data[0] === 0xFF && data[1] === 0xD8) return 'jpg';
    if (data[0] === 0x47 && data[1] === 0x49) return 'gif';
  }
  return 'png';
}

const PAGE_SIZES = {
  Letter: { width: 12240, height: 15840 },
  A4: { width: 11906, height: 16838 },
  A3: { width: 16838, height: 23811 },
  Legal: { width: 12240, height: 20160 },
};

const DEFAULT_MARGINS = { top: INCH, right: INCH, bottom: INCH, left: INCH };

// ── Default styles ──────────────────────────────────
const defaultStyles = {
  default: {
    document: { run: { font: 'Arial', size: 24 } },
  },
  paragraphStyles: [
    {
      id: 'Heading1',
      name: 'Heading 1',
      basedOn: 'Normal',
      next: 'Normal',
      quickFormat: true,
      run: { size: 36, bold: true, font: 'Arial', color: '1a1a2e' },
      paragraph: { spacing: { before: 240, after: 120 }, outlineLevel: 0 },
    },
    {
      id: 'Heading2',
      name: 'Heading 2',
      basedOn: 'Normal',
      next: 'Normal',
      quickFormat: true,
      run: { size: 28, bold: true, font: 'Arial', color: '2d2d44' },
      paragraph: { spacing: { before: 200, after: 100 }, outlineLevel: 1 },
    },
    {
      id: 'Heading3',
      name: 'Heading 3',
      basedOn: 'Normal',
      next: 'Normal',
      quickFormat: true,
      run: { size: 24, bold: true, font: 'Arial', color: '3d3d5c' },
      paragraph: { spacing: { before: 160, after: 80 }, outlineLevel: 2 },
    },
  ],
};

// ── Numbering configs ───────────────────────────────
const defaultNumbering = {
  config: [
    {
      reference: 'bullets',
      levels: [
        {
          level: 0,
          format: LevelFormat.BULLET,
          text: '•',
          alignment: AlignmentType.LEFT,
          style: { paragraph: { indent: { left: 720, hanging: 360 } } },
        },
        {
          level: 1,
          format: LevelFormat.BULLET,
          text: '◦',
          alignment: AlignmentType.LEFT,
          style: { paragraph: { indent: { left: 1440, hanging: 360 } } },
        },
      ],
    },
    {
      reference: 'numbers',
      levels: [
        {
          level: 0,
          format: LevelFormat.DECIMAL,
          text: '%1.',
          alignment: AlignmentType.LEFT,
          style: { paragraph: { indent: { left: 720, hanging: 360 } } },
        },
        {
          level: 1,
          format: LevelFormat.DECIMAL,
          text: '%1.%2.',
          alignment: AlignmentType.LEFT,
          style: { paragraph: { indent: { left: 1440, hanging: 360 } } },
        },
        {
          level: 2,
          format: LevelFormat.DECIMAL,
          text: '%1.%2.%3.',
          alignment: AlignmentType.LEFT,
          style: { paragraph: { indent: { left: 2160, hanging: 360 } } },
        },
      ],
    },
  ],
};

// Export default configs for merging
export { defaultNumbering, defaultStyles };

// ── Dynamic Theming ─────────────────────────────────

/**
 * Generate a complete styles + numbering config from brand colors and font.
 * Pass the returned object's `styles` and `numbering` to createDocument.
 *
 * @param {object} config
 * @param {string} config.primary - Primary color hex (no #), used for headings and table headers
 * @param {string} [config.secondary] - Secondary color hex, used for Heading 2/3
 * @param {string} [config.accent] - Accent color hex, used for callout borders
 * @param {string} [config.neutral] - Neutral color hex, used for body text
 * @param {string} [config.font] - Font family name (default: 'Arial')
 * @returns {{ styles: object, numbering: object, colors: object }}
 */
export function createTheme({ primary, secondary, accent, neutral, font } = {}) {
  const colors = {
    primary: primary || '1a1a2e',
    secondary: secondary || darken(primary || '1a1a2e', 20),
    accent: accent || '0078D4',
    neutral: neutral || '333333',
    font: font || 'Arial',
  };

  const styles = {
    default: {
      document: { run: { font: colors.font, size: 24, color: colors.neutral } },
    },
    paragraphStyles: [
      {
        id: 'Heading1',
        name: 'Heading 1',
        basedOn: 'Normal',
        next: 'Normal',
        quickFormat: true,
        run: { size: 36, bold: true, font: colors.font, color: colors.primary },
        paragraph: { spacing: { before: 240, after: 120 }, outlineLevel: 0 },
      },
      {
        id: 'Heading2',
        name: 'Heading 2',
        basedOn: 'Normal',
        next: 'Normal',
        quickFormat: true,
        run: { size: 28, bold: true, font: colors.font, color: colors.secondary },
        paragraph: { spacing: { before: 200, after: 100 }, outlineLevel: 1 },
      },
      {
        id: 'Heading3',
        name: 'Heading 3',
        basedOn: 'Normal',
        next: 'Normal',
        quickFormat: true,
        run: { size: 24, bold: true, font: colors.font, color: colors.secondary },
        paragraph: { spacing: { before: 160, after: 80 }, outlineLevel: 2 },
      },
    ],
  };

  const numbering = { ...defaultNumbering };

  return { styles, numbering, colors };
}

/** Darken a hex color by a percentage (0-100). */
function darken(hex, percent) {
  const num = parseInt(hex.replace('#', ''), 16);
  const r = Math.max(0, ((num >> 16) & 0xff) - Math.round(255 * (percent / 100)));
  const g = Math.max(0, ((num >> 8) & 0xff) - Math.round(255 * (percent / 100)));
  const b = Math.max(0, (num & 0xff) - Math.round(255 * (percent / 100)));
  return ((r << 16) | (g << 8) | b).toString(16).padStart(6, '0');
}

// ── Table of Contents ───────────────────────────────

/**
 * Create a Table of Contents element. Insert in children[] where the TOC should appear.
 * Requires `features: { updateFields: true }` on the Document for field updates.
 *
 * @param {object} [opts]
 * @param {string} [opts.heading] - Heading text above TOC (default: 'Table of Contents')
 * @param {number} [opts.maxLevel] - Max heading level to include (1-3, default: 3)
 * @param {boolean} [opts.hyperlink] - Include hyperlinks (default: true)
 * @returns {Array} Array of elements (heading + TOC) to spread into children[]
 */
export function toc(opts = {}) {
  const { heading = 'Table of Contents', maxLevel = 3, hyperlink = true } = opts;
  const elements = [];

  if (heading) {
    elements.push(
      new Paragraph({
        heading: HeadingLevel.HEADING_1,
        children: [new TextRun({ text: heading, bold: true })],
        spacing: { after: 200 },
      })
    );
  }

  elements.push(
    new TableOfContents('TOC', {
      hyperlink,
      headingStyleRange: `1-${maxLevel}`,
    })
  );

  return elements;
}

// ── Rich Text Paragraphs ────────────────────────────

/**
 * Create a paragraph with multiple styled text segments and hyperlinks.
 *
 * @param {...object} segments - Objects with { text, bold?, italic?, color?, link?, size?, font?, underline? }
 * @returns {Paragraph}
 */
export function richParagraph(...segments) {
  const children = segments.map(seg => {
    if (seg.link) {
      return new ExternalHyperlink({
        children: [
          new TextRun({
            text: seg.text,
            bold: seg.bold,
            italics: seg.italic,
            color: seg.color || '0563C1',
            size: seg.size,
            font: seg.font,
            underline: seg.underline !== false ? {} : undefined,
            style: 'Hyperlink',
          }),
        ],
        link: seg.link,
      });
    }
    return new TextRun({
      text: seg.text,
      bold: seg.bold,
      italics: seg.italic,
      color: seg.color,
      size: seg.size,
      font: seg.font,
      underline: seg.underline ? {} : undefined,
    });
  });

  return new Paragraph({ children });
}

// ── Multi-Section Layouts ───────────────────────────

/**
 * Create a document with multiple sections, each with independent page settings.
 * Useful for mixing portrait and landscape pages, different margins, or headers/footers.
 *
 * @param {object} opts
 * @param {Array<{properties?: object, children: Array}>} opts.sections - Array of section configs
 * @param {string} [opts.title] - Document title
 * @param {object} [opts.styles] - Document styles
 * @param {object} [opts.numbering] - Document numbering
 * @param {object} [opts.features] - Document features (e.g. { updateFields: true })
 * @returns {Document}
 */
export function createMultiSectionDocument(opts = {}) {
  const {
    sections = [],
    title = 'Document',
    styles = defaultStyles,
    numbering = defaultNumbering,
    features,
  } = opts;

  const docSections = sections.map(section => {
    const { properties = {}, children = [] } = section;
    return { properties, children };
  });

  const docOpts = { styles, numbering, title, sections: docSections };
  if (features) docOpts.features = features;

  return new Document(docOpts);
}

// ── Cover Page ──────────────────────────────────────

/**
 * Generate a cover page section for formal documents.
 * Returns a section object to use in createMultiSectionDocument or as the first section.
 *
 * @param {object} opts
 * @param {string} opts.title - Main title
 * @param {string} [opts.subtitle] - Subtitle text
 * @param {string} [opts.author] - Author name
 * @param {string} [opts.date] - Date string
 * @param {string|Uint8Array} [opts.logo] - Base64 data URL or buffer for logo image
 * @param {object} [opts.colors] - { primary, secondary } hex colors
 * @param {'Letter'|'A4'|'A3'|'Legal'} [opts.pageSize] - Page size (default: 'Letter')
 * @returns {{ properties: object, children: Array }}
 */
export function coverPage(opts = {}) {
  const {
    title = 'Document',
    subtitle,
    author,
    date,
    logo,
    colors = {},
    pageSize = 'Letter',
  } = opts;

  const primaryColor = colors.primary || '1a1a2e';
  const size = PAGE_SIZES[pageSize] || PAGE_SIZES.Letter;

  const children = [];

  // Spacer at top
  children.push(new Paragraph({ spacing: { before: 2000 } }));
  children.push(new Paragraph({ spacing: { before: 1000 } }));

  // Logo if provided
  if (logo) {
    const imgType = detectImageType(logo);
    let imageData = logo;
    if (typeof logo === 'string' && logo.startsWith('data:')) {
      const base64 = logo.split(',')[1];
      const binary = atob(base64);
      const bytes = new Uint8Array(binary.length);
      for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i);
      imageData = bytes;
    }
    children.push(
      new Paragraph({
        alignment: AlignmentType.CENTER,
        spacing: { after: 600 },
        children: [
          new ImageRun({
            type: imgType,
            data: imageData,
            transformation: { width: 150, height: 150 },
            altText: { title: 'Logo', description: 'Company logo', name: 'logo' },
          }),
        ],
      })
    );
  }

  // More spacing
  children.push(new Paragraph({ spacing: { before: 1000 } }));

  // Title
  children.push(
    new Paragraph({
      alignment: AlignmentType.CENTER,
      spacing: { after: 200 },
      children: [
        new TextRun({ text: title, bold: true, size: 72, color: primaryColor }),
      ],
    })
  );

  // Subtitle
  if (subtitle) {
    children.push(
      new Paragraph({
        alignment: AlignmentType.CENTER,
        spacing: { after: 400 },
        children: [
          new TextRun({ text: subtitle, size: 36, color: '666666', italics: true }),
        ],
      })
    );
  }

  // Spacer before metadata
  children.push(new Paragraph({ spacing: { before: 2000 } }));

  // Author
  if (author) {
    children.push(
      new Paragraph({
        alignment: AlignmentType.CENTER,
        spacing: { after: 120 },
        children: [new TextRun({ text: author, size: 28, color: '444444' })],
      })
    );
  }

  // Date
  if (date) {
    children.push(
      new Paragraph({
        alignment: AlignmentType.CENTER,
        spacing: { after: 120 },
        children: [new TextRun({ text: date, size: 24, color: '666666' })],
      })
    );
  }

  return {
    properties: {
      page: {
        size: { width: size.width, height: size.height },
        margin: DEFAULT_MARGINS,
      },
      titlePage: true,
    },
    children,
  };
}

// ── Callout / Info Boxes ────────────────────────────

const CALLOUT_COLORS = {
  info: { border: '0078D4', background: 'E8F4FD' },
  warning: { border: 'F5A623', background: 'FFF8E1' },
  tip: { border: '4CAF50', background: 'E8F5E9' },
  note: { border: '9E9E9E', background: 'F5F5F5' },
};

/**
 * Create a callout/info box paragraph with a colored left border and background shading.
 *
 * @param {string} text - Callout text content
 * @param {object} [opts]
 * @param {'info'|'warning'|'tip'|'note'} [opts.type] - Callout type (default: 'info')
 * @param {object} [opts.theme] - Theme object from createTheme() for accent color override
 * @param {string} [opts.borderColor] - Override border color (hex)
 * @param {string} [opts.backgroundColor] - Override background color (hex)
 * @returns {Paragraph}
 */
export function callout(text, opts = {}) {
  const { type = 'info', theme, borderColor, backgroundColor } = opts;
  const defaults = CALLOUT_COLORS[type] || CALLOUT_COLORS.info;

  const border = borderColor || (theme?.colors?.accent) || defaults.border;
  const background = backgroundColor || defaults.background;

  return new Paragraph({
    spacing: { before: 120, after: 120 },
    indent: { left: 360 },
    border: {
      left: { style: BorderStyle.SINGLE, size: 24, color: border, space: 10 },
    },
    shading: { fill: background, type: ShadingType.CLEAR },
    children: [
      new TextRun({ text, size: 22 }),
    ],
  });
}

// ── Advanced Tables ─────────────────────────────────

/**
 * Create an advanced table with merged cells, custom column widths, and theme integration.
 *
 * @param {object} config
 * @param {string[]} config.headers - Header row cell texts
 * @param {Array<string[]|object[]>} config.rows - Data rows (strings or {text, bold?, color?, shading?} objects)
 * @param {Array<{row: number, col: number, rowSpan?: number, colSpan?: number}>} [config.merges] - Cell merges
 * @param {number[]} [config.columnWidths] - Column widths in DXA
 * @param {object} [config.headerStyle] - { color, textColor, bold } for header row
 * @param {object} [config.theme] - Theme object from createTheme()
 * @param {string} [config.borderColor] - Border color hex
 * @returns {Table}
 */
export function advancedTable(config = {}) {
  const {
    headers = [],
    rows = [],
    merges = [],
    columnWidths,
    headerStyle = {},
    theme,
    borderColor = 'CCCCCC',
  } = config;

  const cols = headers.length || (rows[0]?.length || 1);
  const totalWidth = 9360;
  const colWidths = columnWidths || Array(cols).fill(Math.floor(totalWidth / cols));

  const hColor = headerStyle.color || theme?.colors?.primary || 'D5E8F0';
  const hTextColor = headerStyle.textColor || 'FFFFFF';
  const hBold = headerStyle.bold !== false;

  const border = { style: BorderStyle.SINGLE, size: 1, color: borderColor };
  const borders = { top: border, bottom: border, left: border, right: border };
  const cellPadding = { top: 80, bottom: 80, left: 120, right: 120 };

  // Build merge map for quick lookup: key = "row-col" => { rowSpan, colSpan }
  const mergeMap = {};
  for (const m of merges) {
    mergeMap[`${m.row}-${m.col}`] = { rowSpan: m.rowSpan || 1, colSpan: m.colSpan || 1 };
  }

  // Track which cells are "covered" by a merge (should be skipped)
  const coveredCells = new Set();
  for (const m of merges) {
    for (let r = m.row; r < m.row + (m.rowSpan || 1); r++) {
      for (let c = m.col; c < m.col + (m.colSpan || 1); c++) {
        if (r !== m.row || c !== m.col) coveredCells.add(`${r}-${c}`);
      }
    }
  }

  const allRows = [headers, ...rows];

  const tableRows = allRows.map((row, ri) => {
    const cells = [];
    for (let ci = 0; ci < cols; ci++) {
      if (coveredCells.has(`${ri}-${ci}`)) continue;

      const merge = mergeMap[`${ri}-${ci}`];
      const cellValue = row[ci];
      const isHeader = ri === 0;

      // Parse cell value
      let text, cellBold, cellColor, cellShading;
      if (typeof cellValue === 'object' && cellValue !== null && !Array.isArray(cellValue)) {
        text = cellValue.text || '';
        cellBold = cellValue.bold;
        cellColor = cellValue.color;
        cellShading = cellValue.shading;
      } else {
        text = String(cellValue ?? '');
      }

      const cellOpts = {
        borders,
        margins: cellPadding,
        width: { size: colWidths[ci], type: WidthType.DXA },
        children: [
          new Paragraph({
            children: [
              new TextRun({
                text,
                bold: cellBold ?? (isHeader ? hBold : false),
                color: cellColor ?? (isHeader ? hTextColor : undefined),
              }),
            ],
          }),
        ],
      };

      if (isHeader) {
        cellOpts.shading = { fill: hColor, type: ShadingType.CLEAR };
      } else if (cellShading) {
        cellOpts.shading = { fill: cellShading, type: ShadingType.CLEAR };
      }

      if (merge) {
        if (merge.colSpan > 1) cellOpts.columnSpan = merge.colSpan;
        if (merge.rowSpan > 1) cellOpts.rowSpan = merge.rowSpan;
      }

      cells.push(new TableCell(cellOpts));
    }
    return new TableRow({ children: cells });
  });

  return new Table({
    width: { size: totalWidth, type: WidthType.DXA },
    columnWidths: colWidths,
    rows: tableRows,
  });
}

// ── Multi-Level Lists (Numbering Configs) ───────────

/**
 * Create a numbering configuration for hierarchical/multi-level lists.
 *
 * @param {'outline'|'legal'|'alpha'|'roman'} [style] - Numbering style (default: 'outline')
 * @returns {{ config: Array }} Numbering config to merge into document numbering
 */
export function createNumberingConfig(style = 'outline') {
  const configs = {
    outline: {
      reference: 'outline-list',
      levels: [
        { level: 0, format: LevelFormat.DECIMAL, text: '%1.', alignment: AlignmentType.LEFT, style: { paragraph: { indent: { left: 720, hanging: 360 } } } },
        { level: 1, format: LevelFormat.DECIMAL, text: '%1.%2.', alignment: AlignmentType.LEFT, style: { paragraph: { indent: { left: 1440, hanging: 360 } } } },
        { level: 2, format: LevelFormat.DECIMAL, text: '%1.%2.%3.', alignment: AlignmentType.LEFT, style: { paragraph: { indent: { left: 2160, hanging: 360 } } } },
      ],
    },
    legal: {
      reference: 'legal-list',
      levels: [
        { level: 0, format: LevelFormat.UPPER_ROMAN, text: '%1.', alignment: AlignmentType.LEFT, style: { paragraph: { indent: { left: 720, hanging: 360 } } } },
        { level: 1, format: LevelFormat.UPPER_LETTER, text: '%2.', alignment: AlignmentType.LEFT, style: { paragraph: { indent: { left: 1440, hanging: 360 } } } },
        { level: 2, format: LevelFormat.DECIMAL, text: '%3.', alignment: AlignmentType.LEFT, style: { paragraph: { indent: { left: 2160, hanging: 360 } } } },
      ],
    },
    alpha: {
      reference: 'alpha-list',
      levels: [
        { level: 0, format: LevelFormat.UPPER_LETTER, text: '%1.', alignment: AlignmentType.LEFT, style: { paragraph: { indent: { left: 720, hanging: 360 } } } },
        { level: 1, format: LevelFormat.LOWER_LETTER, text: '%2.', alignment: AlignmentType.LEFT, style: { paragraph: { indent: { left: 1440, hanging: 360 } } } },
        { level: 2, format: LevelFormat.DECIMAL, text: '%3)', alignment: AlignmentType.LEFT, style: { paragraph: { indent: { left: 2160, hanging: 360 } } } },
      ],
    },
    roman: {
      reference: 'roman-list',
      levels: [
        { level: 0, format: LevelFormat.UPPER_ROMAN, text: '%1.', alignment: AlignmentType.LEFT, style: { paragraph: { indent: { left: 720, hanging: 360 } } } },
        { level: 1, format: LevelFormat.LOWER_ROMAN, text: '%2.', alignment: AlignmentType.LEFT, style: { paragraph: { indent: { left: 1440, hanging: 360 } } } },
        { level: 2, format: LevelFormat.DECIMAL, text: '%3.', alignment: AlignmentType.LEFT, style: { paragraph: { indent: { left: 2160, hanging: 360 } } } },
      ],
    },
  };

  const selected = configs[style] || configs.outline;
  return { config: [selected] };
}

// ── Image / capture helpers ─────────────────────────

/**
 * Capture a DOM element as a PNG data-URL using html-to-image.
 * Returns { dataUrl, width, height } ready for `image()` or `ImageRun`.
 *
 * @param {HTMLElement} element - DOM element to capture
 * @param {object} [opts] - { pixelRatio?: number }
 * @returns {Promise<{ dataUrl: string, width: number, height: number }>}
 */
export async function captureElement(element, opts = {}) {
  const { pixelRatio = 2 } = opts;
  const dataUrl = await toPng(element, {
    pixelRatio,
    cacheBust: true,
    skipFonts: true,
    filter: node => !node.classList?.contains?.('no-export'),
  });
  const { width, height } = element.getBoundingClientRect();
  return { dataUrl, width: Math.round(width), height: Math.round(height) };
}

/**
 * Create an image paragraph from a base64 data-URL or ArrayBuffer.
 *
 * @param {string|ArrayBuffer|Uint8Array} data - base64 data-URL string or raw buffer
 * @param {object} opts
 * @param {number} opts.width - Display width in DXA (1 inch = 1440)
 * @param {number} opts.height - Display height in DXA
 * @param {'png'|'jpg'|'gif'|'bmp'} [opts.type] - Image format (default: 'png')
 * @param {string} [opts.altText] - Alt text for accessibility
 * @param {string} [opts.alignment] - Paragraph alignment
 */
export function image(data, opts = {}) {
  const { width, height, type = 'png', altText = 'Image', alignment } = opts;

  // Convert data-URL string to Uint8Array
  let imageData = data;
  if (typeof data === 'string' && data.startsWith('data:')) {
    const base64 = data.split(',')[1];
    const binary = atob(base64);
    const bytes = new Uint8Array(binary.length);
    for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i);
    imageData = bytes;
  }

  return new Paragraph({
    alignment: alignment || AlignmentType.CENTER,
    children: [
      new ImageRun({
        type,
        data: imageData,
        transformation: { width, height },
        altText: { title: altText, description: altText, name: altText },
      }),
    ],
  });
}

/**
 * Convenience: capture a DOM element and return a ready-to-use image Paragraph.
 *
 * @param {HTMLElement} element - DOM element to capture
 * @param {object} [opts] - { maxWidth?: number (DXA, default 9360 = full width), pixelRatio?: number, altText?: string }
 * @returns {Promise<Paragraph>}
 */
export async function captureAsImage(element, opts = {}) {
  const { maxWidth = 9360, pixelRatio = 2, altText = 'Captured content' } = opts;
  const { dataUrl, width, height } = await captureElement(element, { pixelRatio });
  const scale = Math.min(1, maxWidth / width);
  return image(dataUrl, {
    width: Math.round(width * scale),
    height: Math.round(height * scale),
    altText,
  });
}

// ── Builder helpers ─────────────────────────────────

/**
 * Build a paragraph.
 * @param {string} text
 * @param {object} [opts] - { heading, bold, italic, size, color, alignment, spacing, bullet, numbered, level, numberingReference }
 */
export function p(text, opts = {}) {
  const run = new TextRun({
    text,
    bold: opts.bold,
    italics: opts.italic,
    size: opts.size,
    color: opts.color,
    font: opts.font,
  });
  const cfg = { children: [run] };
  if (opts.heading) cfg.heading = opts.heading;
  if (opts.alignment) cfg.alignment = opts.alignment;
  if (opts.spacing) cfg.spacing = opts.spacing;
  if (opts.bullet) cfg.numbering = { reference: 'bullets', level: opts.bulletLevel ?? 0 };
  if (opts.numbered) {
    cfg.numbering = {
      reference: opts.numberingReference || 'numbers',
      level: opts.level ?? 0,
    };
  }
  return new Paragraph(cfg);
}

/**
 * Build a simple table from a 2-d array of strings.
 * First row is treated as headers with shading.
 * @param {string[][]} data
 * @param {object} [opts] - { headerColor, headerTextColor, borderColor, cellPadding }
 */
export function table(data, opts = {}) {
  const {
    headerColor = 'D5E8F0',
    headerTextColor = '1a1a2e',
    borderColor = 'CCCCCC',
    cellPadding = { top: 80, bottom: 80, left: 120, right: 120 },
  } = opts;

  const border = { style: BorderStyle.SINGLE, size: 1, color: borderColor };
  const borders = { top: border, bottom: border, left: border, right: border };

  const cols = data[0]?.length || 1;
  const colWidth = Math.floor(9360 / cols);

  return new Table({
    width: { size: 9360, type: WidthType.DXA },
    columnWidths: Array(cols).fill(colWidth),
    rows: data.map(
      (row, ri) =>
        new TableRow({
          children: row.map(
            cell =>
              new TableCell({
                borders,
                width: { size: colWidth, type: WidthType.DXA },
                shading: ri === 0 ? { fill: headerColor, type: ShadingType.CLEAR } : undefined,
                margins: cellPadding,
                children: [
                  new Paragraph({
                    children: [
                      new TextRun({
                        text: String(cell),
                        bold: ri === 0,
                        color: ri === 0 ? headerTextColor : undefined,
                      }),
                    ],
                  }),
                ],
              })
          ),
        })
    ),
  });
}

// ── Headers/Footers helpers ─────────────────────────

/**
 * Build Header/Footer objects from flexible input (string, Paragraph[], or config with first/default/even).
 * @private
 */
function buildHeaderFooter(config, Constructor) {
  if (!config) return undefined;

  const buildOne = (input) => {
    if (!input) return undefined;
    if (typeof input === 'string') {
      return new Constructor({
        children: [
          new Paragraph({
            children: [new TextRun({ text: input, italics: true, size: 18, color: '888888' })],
            alignment: AlignmentType.RIGHT,
          }),
        ],
      });
    }
    // Assume array of Paragraph elements
    return new Constructor({ children: Array.isArray(input) ? input : [input] });
  };

  if (typeof config === 'string' || Array.isArray(config)) {
    return { default: buildOne(config) };
  }

  const result = {};
  if (config.default) result.default = buildOne(config.default);
  if (config.first) result.first = buildOne(config.first);
  if (config.even) result.even = buildOne(config.even);
  return result;
}

/**
 * Create a full Document and return it ready for packing.
 * @param {object} opts
 * @param {Array} opts.children - Array of Paragraph / Table elements for the body
 * @param {string} [opts.title] - Document title
 * @param {string} [opts.headerText] - Text for running header (simple mode)
 * @param {string|object} [opts.header] - Header config: string, Paragraph[], or { first?, default?, even? }
 * @param {string|object} [opts.footer] - Footer config: string, Paragraph[], or { first?, default?, even? }
 * @param {string|Uint8Array} [opts.logo] - Logo base64/buffer to place in header
 * @param {boolean} [opts.pageNumbers] - Show page numbers in footer (default: true)
 * @param {'Letter'|'A4'|'A3'|'Legal'} [opts.pageSize]
 * @param {'portrait'|'landscape'} [opts.orientation]
 * @param {object} [opts.margins] - { top, right, bottom, left } in DXA
 * @param {object} [opts.styles] - Override default styles
 * @param {object} [opts.numbering] - Override default numbering
 * @param {object} [opts.features] - Document features (e.g. { updateFields: true })
 */
export function createDocument(opts = {}) {
  const {
    children = [],
    title,
    headerText,
    header,
    footer,
    logo,
    pageNumbers = true,
    pageSize = 'Letter',
    orientation = 'portrait',
    margins = DEFAULT_MARGINS,
    styles = defaultStyles,
    numbering = defaultNumbering,
    features,
  } = opts;

  const size = PAGE_SIZES[pageSize] || PAGE_SIZES.Letter;
  const isLandscape = orientation === 'landscape';

  const sectionProps = {
    page: {
      size: {
        width: size.width,
        height: size.height,
        ...(isLandscape ? { orientation: PageOrientation.LANDSCAPE } : {}),
      },
      margin: margins,
    },
  };

  // Headers: support new flexible format or legacy headerText
  let sectionHeaders;
  let defaultHeaderChildren = [];
  if (header) {
    sectionHeaders = buildHeaderFooter(header, Header);
  } else if (headerText) {
    defaultHeaderChildren = [
      new Paragraph({
        children: [new TextRun({ text: headerText, italics: true, size: 18, color: '888888' })],
        alignment: AlignmentType.RIGHT,
      }),
    ];
    sectionHeaders = { default: new Header({ children: defaultHeaderChildren }) };
  }

  // Logo in header
  if (logo && !header) {
    const imgType = detectImageType(logo);
    let imageData = logo;
    if (typeof logo === 'string' && logo.startsWith('data:')) {
      const base64 = logo.split(',')[1];
      const binary = atob(base64);
      const bytes = new Uint8Array(binary.length);
      for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i);
      imageData = bytes;
    }
    const logoParagraph = new Paragraph({
      children: [
        new ImageRun({
          type: imgType,
          data: imageData,
          transformation: { width: 60, height: 60 },
          altText: { title: 'Logo', description: 'Header logo', name: 'logo' },
        }),
      ],
    });
    sectionHeaders = { default: new Header({ children: [logoParagraph, ...defaultHeaderChildren] }) };
  }

  // Footers: support new flexible format or legacy pageNumbers
  let sectionFooters;
  if (footer) {
    sectionFooters = buildHeaderFooter(footer, Footer);
  } else if (pageNumbers) {
    sectionFooters = {
      default: new Footer({
        children: [
          new Paragraph({
            alignment: AlignmentType.CENTER,
            children: [
              new TextRun({
                children: ['Page ', PageNumber.CURRENT, ' of ', PageNumber.TOTAL_PAGES],
                size: 18,
                color: '888888',
              }),
            ],
          }),
        ],
      }),
    };
  }

  const section = { properties: sectionProps, children };
  if (sectionHeaders) section.headers = sectionHeaders;
  if (sectionFooters) section.footers = sectionFooters;

  const docOpts = {
    styles,
    numbering,
    title: title || 'Document',
    sections: [section],
  };

  if (features) docOpts.features = features;

  const doc = new Document(docOpts);
  return doc;
}

// ── React hook ──────────────────────────────────────

/**
 * Hook for exporting data as a .docx file.
 *
 * @param {object} [options]
 * @param {'Letter'|'A4'|'A3'|'Legal'} [options.pageSize]
 * @param {'portrait'|'landscape'} [options.orientation]
 *
 * @returns {{ exportToDocx: (doc: Document, filename?: string) => Promise<void>, isExporting: boolean }}
 */
export const useDocxExport = (options = {}) => {
  const [isExporting, setIsExporting] = useState(false);

  const exportToDocx = useCallback(async (doc, filename = 'document.docx') => {
    setIsExporting(true);
    try {
      const blob = await Packer.toBlob(doc);
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    } catch (error) {
      console.error('DOCX Export Error:', error);
      throw error;
    } finally {
      setIsExporting(false);
    }
  }, []);

  return { exportToDocx, isExporting };
};

// Re-export useful docx primitives so the app file can build complex docs
export {
  Document,
  Packer,
  Paragraph,
  TextRun,
  Table,
  TableRow,
  TableCell,
  ImageRun,
  Header,
  Footer,
  AlignmentType,
  HeadingLevel,
  BorderStyle,
  WidthType,
  ShadingType,
  PageNumber,
  PageBreak,
  LevelFormat,
  ExternalHyperlink,
  TableOfContents,
  PageOrientation,
  SectionType,
  VerticalAlign,
};
